/**
 * Backend claim statuses → player UI buckets.
 *
 * Active = in-progress (player can still earn/use it).
 * History = terminal (player should see it but can't act).
 *
 * The status list is no longer restated here. It comes from
 * `@b1fr0st/contracts/domain/promo-claim-status`, which a test reads against the
 * svc-promo sources — the previous copy was a second hand-typed list checked
 * only against a third one in this file's own test, so a status the backend
 * writes and neither list named was invisible to every test here. That is how
 * `revoked` went unbucketed: `bucketOf` answered null, the claim appeared in
 * neither the active list nor the history list, and `isPromoStillOffered` kept
 * offering a Claim button the server would refuse.
 *
 * `BUCKET` is keyed on the shared status union, so a new backend status stops
 * this file from compiling until someone says which side of the line it falls.
 */
import {
  PROMOTION_CLAIM_STATUSES,
  isPromotionClaimStatus,
  type PromotionClaimStatus,
} from '@b1fr0st/contracts/domain/promo-claim-status';

export type ClaimStatus = PromotionClaimStatus;

export const ALL_BACKEND_STATUSES = PROMOTION_CLAIM_STATUSES;

const BUCKET: Record<ClaimStatus, 'active' | 'history'> = {
  pending: 'active',
  approved: 'active',
  active: 'active',
  completed: 'history',
  released: 'history',
  rejected: 'history',
  forfeited: 'history',
  revoked: 'history',
  cancelled: 'history',
};

export const ACTIVE_STATUSES: ReadonlySet<string> = new Set(
  ALL_BACKEND_STATUSES.filter((s) => BUCKET[s] === 'active'),
);
export const HISTORY_STATUSES: ReadonlySet<string> = new Set(
  ALL_BACKEND_STATUSES.filter((s) => BUCKET[s] === 'history'),
);

export function bucketOf(status: string | null | undefined): 'active' | 'history' | null {
  return isPromotionClaimStatus(status) ? BUCKET[status] : null;
}

/**
 * True if this claim has already ENDED — it belongs to the player's history and
 * nothing further is coming from it.
 *
 * The claim endpoint is idempotent: a repeated tap re-returns the row the member
 * already holds rather than refusing it, and that row may be one that ended
 * (forfeited at expiry, revoked by an admin, completed, released, cancelled).
 * Such a response is an ECHO, not a grant. It still carries the original
 * `grantedAmount`, so a caller that reads the amount without the status
 * announces money that is not arriving — a forfeited ฿50 reported as ฿50
 * received, on the very tap that was refused before the endpoint became
 * idempotent.
 */
export function isHistoricalClaim(status: string | null | undefined): boolean {
  return bucketOf(status) === 'history';
}

/** True if `status` still occupies a single-grant kind's claim slot server-side
 *  — mirrors PRIOR_CLAIM_EXCLUDED_STATUSES in
 *  apps/promo/src/repo/claim-eligibility.ts. Only 'rejected' clears the
 *  per-member dedup index, so only 'rejected' should be excluded here. */
export function blocksSingleGrantReclaim(status: string | null | undefined): boolean {
  return status !== 'rejected';
}
